import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { CritereAttache } from 'src/app/utils/interfaces/new/CritereAttache';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Maquette } from 'src/app/utils/interfaces/new/Maquette';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../filtres/filtres.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import { ImportMaquetteObject } from 'src/app/utils/interfaces/new/import-maquette.interface';

@Injectable({
  providedIn: 'root'
})
export class AttacheService {

  private isMock = environment.mock;
  private _wait = (ms) => new Promise(res => setTimeout(res, ms));
  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders()
  };
  private uri = environment.basePath + "/personnalisation/attache";
  private uriExport = environment.basePath + '/personnalisation/maquettes'
  private uriTextesExport = environment.basePath + '/personnalisation/textes'

  constructor(
    private http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService
  ) { }

  public async create(attache: Array<CritereAttache>): Promise<void> {
    const res = await this.http.post(this.uri, attache, this.httpOptions).toPromise();
  }

  public async findByMaquetteId(maquetteId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortBy: 'order',
    sortOrder: SortEnum.ASC
  }): Promise<Paginated<CritereAttache>> {
    try {
      this.httpOptions.headers = this._filtresService.setHeaders(filtre);
      const res = await this.http.get(this.uri + "/maquette/" + maquetteId, this.httpOptions).toPromise();
      const attaches = res.body as CritereAttache[];
      const paginated: Paginated<CritereAttache> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: attaches
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findByCritereId(critereId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortBy: 'order',
    sortOrder: SortEnum.ASC
  }): Promise<Paginated<CritereAttache>> {
    try {
      this.httpOptions.headers = this._filtresService.setHeaders(filtre);
      const res = await this.http.get(this.uri + "/critere/" + critereId, this.httpOptions).toPromise();
      const attaches = res.body as CritereAttache[];
      const paginated: Paginated<CritereAttache> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: attaches
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async update(attache: CritereAttache) {
    try {
      const res = await this.http.put(this.uri + "/" + attache.id, attache, this.httpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOne(attacheId: string): Promise<CritereAttache> {
    try {
      const res = await this.http.get(this.uri + "/" + attacheId, this.httpOptions).toPromise();
      const critere = res.body as CritereAttache;
      return critere;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async duplicateMaquette(maquetteId: string): Promise<Maquette> {
    try {
      const res = await this.http.post(this.uri + "/duplicate/" + maquetteId, undefined, this.httpOptions).toPromise();
      const dupMaquette = res.body as Maquette;
      return dupMaquette;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async exportMaquette(maquetteId: string): Promise<ImportMaquetteObject> {
    try {
      const res = await this.http.get(this.uriExport + "/export/" + maquetteId, this.httpOptions).toPromise();
      const exportMaq = res.body as ImportMaquetteObject;
      return exportMaq;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async importMaquette(maquette: ImportMaquetteObject): Promise<MaquetteImportStatus> {
    try {
      const res = await this.http.post(this.uriExport + "/import", maquette, this.httpOptions).toPromise();
      return res.body as MaquetteImportStatus;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async importTextes(textes: Array<TexteImport>): Promise<TexteImportResult> {
    try {
      const res = await this.http.post(this.uriTextesExport + "/import", textes, this.httpOptions).toPromise();
      return res.body as TexteImportResult;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async delete(attacheId: string) {
    try {
      const res = await this.http.delete(this.uri + "/" + attacheId, this.httpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }
}


export interface MaquetteImportStatus {
  status: StatusEnum;
  critereDoublons?: string[];
}

export enum StatusEnum {
  Ok = "OK",
  DoublonMaquetteName = "DOUBLON_MAQUETTE_NAME",
  DoublonCritereName = "DOUBLON_CRITERE_NAME",
}

export interface TexteImportResult {
  status: TexteImportResultStatusEnum;
  doublons?: string[];
}

export enum TexteImportResultStatusEnum {
  Ok = "OK",
  Doublons = "DOUBLONS",
}

export interface TexteImport {
  titre: string;
  type: TexteImportTypeEnum;
  texte: string;
}

export enum TexteImportTypeEnum {
  Consultation = "consultation",
  Courrier = "courrier",
  Glossaire = "glossaire",
  Pensebete = "pensebete",
}

